import {action, computed, makeObservable, observable, runInAction} from "mobx";

import {AdminApi} from "../../../api/api";
import {TemplateItem, TemplateItemsWithItemIdOutput} from "../../../api/values/template-item";
import {TemplateItemListOutput} from "../../../api/values/template-item-list-output";
import {Stores} from "../../../stores";
import {Permission} from "../../../stores/authentication-store";
import {DataType, STRING_TYPE} from "../../../stores/database-template/database-form-base";
import {PageStoreConstructorOptions} from "../../../utils/page-store";
import {PageRouter} from "../../../utils/route-util";
import {ColumnDef} from "../../atoms/tables/table";
import {deleteDialogStore} from "../../organisms/delete-dialog/delete-dialog-store";

type TableRow = {
    id: string;
    slug: string;
    label: string;
    type: DataType;
    note: string;
    deleted: boolean;
};

export class DatabaseTemplateDetailPageStore {
    /**
     * Observables
     */
    @observable public mediaId = "";
    @observable public templateId = "";
    @observable public templateName = "";
    @observable public templateNote = "";
    @observable public selectedTab: "template" | "management" = "template";
    @observable public templateColumnList: TableRow[] = [];
    @observable public templateItemList = new TemplateItemListOutput({
        templateId: "",
        itemList: [],
    });

    /**
     * テーブルヘッダー
     * - template: テンプレートの項目一覧
     */
    private static readonly TEMPLATE_COLUMNS: ColumnDef[] = [
        {label: "項目ID", width: 187, required: false},
        {label: "項目名", width: 187, required: false},
        {label: "データの種類", width: 112, required: false},
        {label: "説明", width: 694, required: false},
    ];

    /**
     * Constructor
     */
    constructor(options: PageStoreConstructorOptions) {
        makeObservable(this);
        this.adminApi = options.adminApi;
        this.stores = options.stores;
        this.pageRouter = options.pageRouter;

        // 初期タブパラメータ
        const tabParam = this.pageRouter.getQueryParam("tab");
        runInAction(() => {
            if (tabParam === "management") {
                this.selectedTab = "management";
            }
        });
    }

    protected adminApi: AdminApi;
    protected stores: Stores;
    protected pageRouter: PageRouter;

    /**
     *  テーブルヘッダーの定義
     *  - template: テンプレートの項目一覧
     *  - management: データ管理の項目一覧
     */
    @computed
    public get columns(): ColumnDef[] {
        return this.selectedTab === "template"
            ? DatabaseTemplateDetailPageStore.TEMPLATE_COLUMNS
            : this.buildManagementColumns();
    }

    /**
     *  メディアIDを設定
     *  @param {string} id - メディアID
     */
    @action
    public setMediaId(id: string) {
        this.mediaId = id;
    }

    /**
     *  テンプレートIDを設定
     *  @param {string} id - テンプレートID
     */
    @action
    public setTemplateId(id: string) {
        this.templateId = id;
    }

    /**
     *  タブを切り替える
     *  - template: テンプレートの項目一覧
     *  - management: データ管理の項目一覧
     */
    @action.bound
    public setTab(tab: "template" | "management") {
        this.selectedTab = tab;
        const path = `/admin/${this.mediaId}/database-templates/detail/${this.templateId}`;
        this.pageRouter.pushRoute(path, {tab});
    }

    /**
     *  ページ遷移
     *  - toEditPage: テンプレート編集ページ
     *  - toDataRecordCreatePage: データレコード作成ページ
     *  - toDataRecordEditPage: データレコード編集ページ
     */

    @action.bound
    public toEditPage() {
        this.pageRouter.pushRoute(
            `/admin/${this.mediaId}/database-templates/detail/${this.templateId}/edit`,
        );
    }

    @action.bound
    public toDataRecordCreatePage() {
        this.pageRouter.pushRoute(
            `/admin/${this.mediaId}/database-templates/detail/${this.templateId}/record/create`,
        );
    }

    @action.bound
    public toDataRecordEditPage(itemId: string) {
        this.pageRouter.pushRoute(
            `/admin/${this.mediaId}/database-templates/detail/${this.templateId}/record/${itemId}/edit`,
        );
    }

    /**
     *  データ削除の確認ダイアログを開く
     */
    @action.bound
    public confirmDeleteTemplate(itemId: string, itemLabel: string) {
        const message = `<strong>「${itemLabel}」</strong>を削除します。よろしいですか？<br/>この操作は取り消すことができません。`;
        deleteDialogStore.open("データ削除", message, async () => {
            await this.deleteTemplateItem(itemId);
            await this.load();
        });
    }

    /**
     *  初期ロード
     */
    public async initialize({query}: {query: {mediaId: string; templateId: string}}) {
        this.stores.authenticationStore.checkAnyPermissionExists(
            Permission.MANAGE_SYSTEM,
            Permission.MANAGE_MEDIA,
        );
        this.setMediaId(query.mediaId);
        this.setTemplateId(query.templateId);
        await this.load();
    }

    /**
     *  データ取得
     * *  - テンプレートの詳細情報を取得
     * *  - テンプレートの項目一覧を取得
     */
    @action.bound
    public async load() {
        try {
            const template = await this.adminApi.getTemplate(
                {mediaId: this.mediaId},
                {templateId: this.templateId},
            );

            const rawTemplateListItem = await this.adminApi.getTemplateItemList(
                {mediaId: this.mediaId},
                {templateId: this.templateId},
            );

            const templateListItem = this.filterActiveTemplateItems(rawTemplateListItem);

            runInAction(() => {
                this.templateName = template.title;
                this.templateNote = template.note;
                this.templateColumnList = template.templateColumnList
                    .filter((c) => !c.deleted)
                    .map((c) => ({
                        id: c.id,
                        slug: c.slug,
                        label: c.label,
                        type: c.type,
                        note: c.note,
                        deleted: c.deleted,
                    }));
                // 並び替え後をセット
                this.templateItemList = templateListItem;
                // ★ 管理タブ用のカラム幅計算をここで実行
                if (this.selectedTab === "management") {
                    this.buildManagementColumns();
                }
            });
        } catch (e) {
            throw new Error("Error loading template: " + e);
        }
    }

    /**
     * 管理タブのTableヘッダーを構築
     * - テンプレートの項目一覧を基に、列定義を生成
     * - 各列の幅を計算して設定
     * @returns {ColumnDef[]} 管理タブのTableヘッダー
     */
    private buildManagementColumns(): ColumnDef[] {
        const firstWidth = 50;
        const labelWidth = 150;
        const lastWidth = 90;
        const stringTypeWidth = 300; // 文字列型の場合は幅を設定
        const columns = [
            {label: "", width: firstWidth, required: false},
            {label: "ラベル", width: labelWidth, required: false},
            ...this.templateColumnList.map((r) => {
                if (r.type === STRING_TYPE) {
                    return {label: r.label, width: stringTypeWidth, required: false};
                }
                return {label: r.label, required: false};
            }),
            {label: "", width: lastWidth, required: false},
        ];
        return columns;
    }

    private filterActiveTemplateItems(src: TemplateItemListOutput): TemplateItemListOutput {
        return new TemplateItemListOutput({
            templateId: src.templateId,
            itemList: src.itemList
                .map(
                    (group) =>
                        new TemplateItemsWithItemIdOutput({
                            itemId: group.itemId,
                            ...(group.itemLabel ? {itemLabel: group.itemLabel} : {}),
                            items: group.items
                                .filter((it) => it.deleted === false)
                                .map((it) => new TemplateItem(it)),
                        }),
                )
                // すべて削除済みになったグループは除外
                .filter((g) => g.items.length > 0),
        });
    }

    /**
     *  削除APIコール
     *  - テンプレートのデータレコードを削除
     */
    private async deleteTemplateItem(itemId: string) {
        try {
            await this.adminApi.deleteTemplateItem(
                {mediaId: this.mediaId},
                {templateId: this.templateId, itemId},
            );
        } catch (err) {
            console.error("Error deleting template:", err);
        }
    }
}
